import { useState } from "react"
import { ChevronRight, X } from "lucide-react"
import type { RecentParcel } from "@/types/explore"
import { formatNumber } from "@/lib/explore/utils/format-helpers"
import { formatRelativeTime } from "@/lib/explore/utils/format-relative-time"
import { readVersioned, writeVersioned } from "@/lib/explore/utils/versioned-storage"

const STORAGE_KEY = "explorerV2_recentParcelsCollapsed"
const STORAGE_VERSION = 1

interface RecentParcelsListProps {
  recentParcels: RecentParcel[]
  onSelectRecent: (parcel: RecentParcel) => void
  onRemoveRecent?: (idu: string) => void
}

export const RecentParcelsList = ({ recentParcels, onSelectRecent, onRemoveRecent }: RecentParcelsListProps) => {
  const [collapsed, setCollapsed] = useState(() => readVersioned(STORAGE_KEY, STORAGE_VERSION, false))

  const toggleCollapsed = () => {
    setCollapsed((prev: boolean) => {
      const next = !prev
      writeVersioned(STORAGE_KEY, STORAGE_VERSION, next)
      return next
    })
  }

  return (
    <div data-tour-step="recent-parcels" className="flex-1 min-h-0 flex flex-col">
      <button
        onClick={toggleCollapsed}
        className="w-full px-2.5 pt-2 shrink-0 border-t border-gray-100 mx-2.5 cursor-pointer"
      >
        <div className="text-sm font-bold text-primary-light uppercase tracking-wider mb-1 flex items-center gap-1">
          <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${collapsed ? "" : "rotate-90"}`} />
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Parcelles recentes ({recentParcels.length})
        </div>
      </button>
      {!collapsed && (
        <div className="flex-1 overflow-y-auto scrollbar-thin px-2.5 pb-2.5">
          <div className="space-y-0.5">
            {recentParcels.map((rp, i) => (
              <button
                key={i}
                onClick={() => onSelectRecent(rp)}
                className="group w-full cursor-pointer flex items-center gap-2 px-2 py-2 text-left hover:bg-primary-lighter rounded-lg transition-colors"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-primary/[0.08] text-primary rounded-lg flex items-center justify-center text-sm font-bold group-hover:bg-primary/[0.15] transition-colors">
                  {rp.section}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">
                    {rp.section} {rp.number}
                    <span className="text-gray-400 ml-1">
                      {rp.inseeCode} {rp.commune}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400 truncate">
                    {formatNumber(rp.surface)} m{"\u00B2"}
                    {rp.visitedAt ? ` \u00B7 ${formatRelativeTime(rp.visitedAt)}` : ""}
                  </div>
                </div>
                {onRemoveRecent && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemoveRecent(rp.idu)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation()
                        onRemoveRecent(rp.idu)
                      }
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0 cursor-pointer"
                    title="Retirer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
